import {
  collectNavRoutesForAdminAgent,
  type AdminAgentContext,
  type IAdminStore,
} from '@mcn-platform/models';

import { pathnameToAdminAgentContext } from './pathnameToAdminAgentContext';

function entityDisplayName(item: unknown): string | undefined {
  if (item && typeof item === 'object' && 'name' in item) {
    const n = (item as { name: unknown }).name;
    if (typeof n === 'string' && n.trim()) return n.trim();
  }
  return undefined;
}

function orgScopedItem(
  store: IAdminStore,
  model: 'course' | 'product' | 'program',
  entityId: string | undefined,
  organizationId: string | undefined,
): unknown {
  if (!entityId || !organizationId) return undefined;
  const key = `${organizationId}~${entityId}`;
  return store.getItem(model, key);
}

/**
 * Map `/app/...` paths to a short label for the system prompt (no PII).
 */
export function routePathToPageLabel(pathname: string): string | undefined {
  const norm = pathname.replace(/\/+$/, '');
  const parts = norm.split('/').filter(Boolean);
  const appIdx = parts.indexOf('app');
  if (appIdx === -1) return undefined;
  const seg = (i: number) => parts[appIdx + i] || '';

  if (seg(0) === 'course-editor' && seg(1) === 'new') return 'Course editor (new)';
  if (seg(0) === 'course-editor') return 'Course editor';
  if (seg(0) === 'program-editor' && seg(1) === 'new') return 'Program editor (new)';
  if (seg(0) === 'program-editor') return 'Program editor';
  if (seg(0) === 'product-editor' && seg(1) === 'new') return 'Product editor (new)';
  if (seg(0) === 'product-editor') return 'Product editor';
  if (seg(0) === 'microcast-editor') return 'Microcast editor';
  if (seg(0) === 'document-editor') return 'Document editor';
  if (seg(0) === 'web-page-editor') return 'Web page editor';
  if (seg(0) === 'category-editor') return 'E‑commerce category editor';
  if (seg(0) === 'asset') return 'Asset editor';
  if (seg(0) === 'courses') return 'Courses';
  if (seg(0) === 'programs') return 'Programs';
  if (seg(0) === 'microcasts') return 'Microcasts';
  if (seg(0) === 'documents') return 'Documents';
  if (seg(0) === 'products') return 'Products';
  if (seg(0) === 'ecommerce-categories') return 'E‑commerce categories';
  if (seg(0) === 'web-pages') return 'Web pages';
  if (seg(0) === 'portals') return 'Portals';
  if (seg(0) === 'instructors') return 'Instructors';
  if (seg(0) === 'instructor') return 'Instructor editor';
  if (seg(0) === 'certifications') return 'Certifications';
  if (seg(0) === 'certification') return 'Certification editor';
  if (seg(0)?.startsWith('assets/')) return 'Assets library';
  if (seg(0) === 'learning-builder') return 'Learning builder';
  if (seg(0) === 'messaging') return 'Messaging';

  return seg(0) ? `Admin: ${seg(0)}` : 'Admin';
}

/**
 * Full context for each agent request: route ids from the URL plus optional names from MST.
 */
export function buildAdminAgentRequestContext(pathname: string, store: IAdminStore): AdminAgentContext {
  const base = pathnameToAdminAgentContext(pathname);
  const ctx: AdminAgentContext = { ...base };

  const org = store.currentOrganization;
  const orgId = org?.id;
  if (org && typeof org.name === 'string' && org.name.trim()) {
    ctx.organizationName = org.name.trim();
  }

  const portal = store.currentPortal;
  if (portal && typeof portal.name === 'string' && portal.name.trim()) {
    ctx.portalName = portal.name.trim();
  }

  if (typeof document !== 'undefined' && document.title?.trim()) {
    ctx.pageTitle = document.title.trim();
  }

  const label = routePathToPageLabel(pathname);
  if (label) ctx.pageLabel = label;

  if (base.courseId) {
    const name = entityDisplayName(orgScopedItem(store, 'course', base.courseId, orgId));
    if (name) ctx.courseName = name;
  }
  if (base.productId) {
    const name = entityDisplayName(orgScopedItem(store, 'product', base.productId, orgId));
    if (name) ctx.productName = name;
  }
  if (base.programId) {
    const name = entityDisplayName(orgScopedItem(store, 'program', base.programId, orgId));
    if (name) ctx.programName = name;
  }

  const roles = store.currentUserRoles ?? [];
  ctx.navRoutes = collectNavRoutesForAdminAgent(roles, org ?? undefined, { maxRoutes: 120 });

  return ctx;
}
